import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown,
  TrendingUp, TrendingDown, Brain,
  BarChart3, PieChart, Briefcase, Building2,
  Calendar, DollarSign, ExternalLink, Eye,
  FileText, Award, Target, Shield, Globe,
  ChevronLeft, ChevronRight, Layers, Activity
} from 'lucide-react';
import './InstitutionProfilePage.css';

// =============================================================================
// MOCK DATA — keyed by institution name slug
// =============================================================================

const institutionData = {
  'Vanguard Group Inc': {
    name: 'Vanguard Group Inc', cik: '0000102909', type: 'Investment Advisor',
    headquarters: 'Malvern, PA', founded: '1975', ceo: 'Salim Ramji',
    aum: 8600000000000, portfolioValue: 4280000000000,
    totalHoldings: 4521, topHolding: 'AAPL', topWeight: 7.2,
    qoqChange: 3.01, yoyChange: 14.8, turnoverRate: 4.2,
    newPositions: 142, closedPositions: 87, increasedPositions: 1856, decreasedPositions: 1402,
    bio: 'The world\'s largest provider of mutual funds and the second-largest provider of ETFs. Known for low-cost index investing and fiduciary approach.',
    filingHistory: [
      { quarter: 'Q4 2025', value: 4280000000000, change: 3.01, holdings: 4521, filed: '2026-02-05' },
      { quarter: 'Q3 2025', value: 4155000000000, change: 2.45, holdings: 4478, filed: '2025-11-14' },
      { quarter: 'Q2 2025', value: 4056000000000, change: -1.12, holdings: 4512, filed: '2025-08-14' },
      { quarter: 'Q1 2025', value: 4102000000000, change: 5.23, holdings: 4490, filed: '2025-05-15' },
      { quarter: 'Q4 2024', value: 3898000000000, change: 4.67, holdings: 4456, filed: '2025-02-14' },
      { quarter: 'Q3 2024', value: 3724000000000, change: 1.89, holdings: 4423, filed: '2024-11-14' },
    ],
    sectorAllocation: [
      { sector: 'Technology', weight: 31.8, change: 1.2 },
      { sector: 'Healthcare', weight: 13.2, change: -0.4 },
      { sector: 'Financials', weight: 12.9, change: 0.8 },
      { sector: 'Consumer Disc.', weight: 10.5, change: 0.3 },
      { sector: 'Communication', weight: 9.1, change: 0.6 },
      { sector: 'Industrials', weight: 8.2, change: -0.2 },
      { sector: 'Energy', weight: 4.8, change: -1.1 },
      { sector: 'Consumer Staples', weight: 4.5, change: 0.1 },
      { sector: 'Utilities', weight: 2.8, change: 0.4 },
      { sector: 'Real Estate', weight: 2.2, change: -0.1 },
    ],
    topHoldings: [
      { ticker: 'AAPL', company: 'Apple Inc.', shares: 1284935000, value: 382400000000, weight: 7.2, change: 2.1, sharesChange: 15200000 },
      { ticker: 'MSFT', company: 'Microsoft Corp.', shares: 892456000, value: 365800000000, weight: 6.9, change: 1.8, sharesChange: 8900000 },
      { ticker: 'AMZN', company: 'Amazon.com Inc.', shares: 623400000, value: 142200000000, weight: 5.4, change: 3.2, sharesChange: 12400000 },
      { ticker: 'NVDA', company: 'NVIDIA Corp.', shares: 717942580, value: 347210000000, weight: 6.5, change: 8.4, sharesChange: 45600000 },
      { ticker: 'GOOGL', company: 'Alphabet Inc.', shares: 456800000, value: 89200000000, weight: 4.1, change: -0.8, sharesChange: -3200000 },
      { ticker: 'META', company: 'Meta Platforms', shares: 312500000, value: 191300000000, weight: 3.6, change: 4.5, sharesChange: 8700000 },
      { ticker: 'BRK.B', company: 'Berkshire Hathaway B', shares: 289400000, value: 132800000000, weight: 2.5, change: 0.9, sharesChange: 2100000 },
      { ticker: 'TSLA', company: 'Tesla Inc.', shares: 234100000, value: 92400000000, weight: 2.2, change: -3.4, sharesChange: -8900000 },
      { ticker: 'UNH', company: 'UnitedHealth Group', shares: 178900000, value: 95600000000, weight: 1.8, change: 1.2, sharesChange: 3400000 },
      { ticker: 'JPM', company: 'JPMorgan Chase', shares: 267800000, value: 66500000000, weight: 1.6, change: 2.8, sharesChange: 5600000 },
      { ticker: 'V', company: 'Visa Inc.', shares: 189200000, value: 60300000000, weight: 1.4, change: 0.6, sharesChange: 1200000 },
      { ticker: 'JNJ', company: 'Johnson & Johnson', shares: 312400000, value: 48900000000, weight: 1.2, change: -1.5, sharesChange: -4800000 },
      { ticker: 'XOM', company: 'Exxon Mobil', shares: 456700000, value: 50200000000, weight: 1.2, change: -2.1, sharesChange: -7200000 },
      { ticker: 'PG', company: 'Procter & Gamble', shares: 278900000, value: 46800000000, weight: 1.1, change: 0.3, sharesChange: 890000 },
      { ticker: 'HD', company: 'Home Depot', shares: 123400000, value: 49200000000, weight: 1.0, change: 1.8, sharesChange: 2300000 },
    ],
    recentMoves: [
      { ticker: 'NVDA', action: 'Added', shares: 45600000, value: 6490000000, date: '2025-12-31' },
      { ticker: 'AMZN', action: 'Added', shares: 12400000, value: 2830000000, date: '2025-12-31' },
      { ticker: 'AAPL', action: 'Added', shares: 15200000, value: 4520000000, date: '2025-12-31' },
      { ticker: 'TSLA', action: 'Reduced', shares: -8900000, value: -3510000000, date: '2025-12-31' },
      { ticker: 'XOM', action: 'Reduced', shares: -7200000, value: -792000000, date: '2025-12-31' },
      { ticker: 'JNJ', action: 'Reduced', shares: -4800000, value: -751000000, date: '2025-12-31' },
    ],
    aiInsight: "Vanguard's Q4 2025 filing shows continued rotation into AI/tech exposure with NVDA receiving the largest share increase (+45.6M). The firm reduced energy (XOM -7.2M shares) and healthcare (JNJ -4.8M shares) positions, consistent with their passive index rebalancing methodology. Portfolio turnover remains low at 4.2%, confirming their long-term buy-and-hold approach. The overall portfolio grew 3.01% QoQ, slightly underperforming the S&P 500's 3.8% gain.",
  },
  'Citadel Advisors LLC': {
    name: 'Citadel Advisors LLC', cik: '0001423053', type: 'Hedge Fund',
    headquarters: 'Miami, FL', founded: '1990', ceo: 'Kenneth Griffin',
    aum: 620000000000, portfolioValue: 456000000000,
    totalHoldings: 8921, topHolding: 'NVDA', topWeight: 3.1,
    qoqChange: 12.87, yoyChange: 34.2, turnoverRate: 42.8,
    newPositions: 1245, closedPositions: 987, increasedPositions: 3456, decreasedPositions: 2890,
    bio: 'One of the world\'s largest and most profitable hedge funds, founded by Kenneth Griffin. Known for quantitative multi-strategy approach across equities, fixed income, commodities, and macro.',
    filingHistory: [
      { quarter: 'Q4 2025', value: 456000000000, change: 12.87, holdings: 8921, filed: '2026-01-31' },
      { quarter: 'Q3 2025', value: 404000000000, change: 8.45, holdings: 8234, filed: '2025-11-14' },
      { quarter: 'Q2 2025', value: 372500000000, change: -2.34, holdings: 7856, filed: '2025-08-14' },
      { quarter: 'Q1 2025', value: 381400000000, change: 15.67, holdings: 8102, filed: '2025-05-15' },
      { quarter: 'Q4 2024', value: 329700000000, change: 9.23, holdings: 7654, filed: '2025-02-14' },
      { quarter: 'Q3 2024', value: 301800000000, change: 5.12, holdings: 7234, filed: '2024-11-14' },
    ],
    sectorAllocation: [
      { sector: 'Technology', weight: 28.4, change: 3.8 },
      { sector: 'Healthcare', weight: 16.2, change: -1.2 },
      { sector: 'Financials', weight: 14.8, change: 0.6 },
      { sector: 'Consumer Disc.', weight: 12.1, change: 1.4 },
      { sector: 'Communication', weight: 8.5, change: 2.1 },
      { sector: 'Industrials', weight: 7.8, change: -0.8 },
      { sector: 'Energy', weight: 5.2, change: -2.4 },
      { sector: 'Consumer Staples', weight: 3.1, change: -0.3 },
      { sector: 'Materials', weight: 2.4, change: 0.5 },
      { sector: 'Utilities', weight: 1.5, change: -0.2 },
    ],
    topHoldings: [
      { ticker: 'NVDA', company: 'NVIDIA Corp.', shares: 3200000, value: 456000000, weight: 3.1, change: 999, sharesChange: 3200000 },
      { ticker: 'SPY', company: 'SPDR S&P 500 ETF', shares: 12400000, value: 7450000000, weight: 2.8, change: -12.4, sharesChange: -1800000 },
      { ticker: 'QQQ', company: 'Invesco QQQ Trust', shares: 8900000, value: 4450000000, weight: 2.6, change: 8.9, sharesChange: 720000 },
      { ticker: 'META', company: 'Meta Platforms', shares: 1450000, value: 887000000, weight: 2.4, change: 22.1, sharesChange: 262000 },
      { ticker: 'AMZN', company: 'Amazon.com Inc.', shares: 3400000, value: 776000000, weight: 2.2, change: 15.6, sharesChange: 460000 },
      { ticker: 'TSLA', company: 'Tesla Inc.', shares: 2100000, value: 828900000, weight: 2.0, change: -34.5, sharesChange: -1100000 },
      { ticker: 'AAPL', company: 'Apple Inc.', shares: 2800000, value: 832000000, weight: 1.8, change: 5.4, sharesChange: 145000 },
      { ticker: 'MSFT', company: 'Microsoft Corp.', shares: 1200000, value: 491000000, weight: 1.5, change: -8.2, sharesChange: -108000 },
      { ticker: 'GOOGL', company: 'Alphabet Inc.', shares: 2400000, value: 468000000, weight: 1.4, change: 18.7, sharesChange: 380000 },
      { ticker: 'PLTR', company: 'Palantir Technologies', shares: 4500000, value: 371700000, weight: 1.2, change: 999, sharesChange: 4500000 },
    ],
    recentMoves: [
      { ticker: 'NVDA', action: 'New Position', shares: 3200000, value: 456000000, date: '2025-12-31' },
      { ticker: 'PLTR', action: 'New Position', shares: 4500000, value: 371700000, date: '2025-12-31' },
      { ticker: 'META', action: 'Added', shares: 262000, value: 160200000, date: '2025-12-31' },
      { ticker: 'TSLA', action: 'Reduced', shares: -1100000, value: -434000000, date: '2025-12-31' },
      { ticker: 'SPY', action: 'Reduced', shares: -1800000, value: -1080000000, date: '2025-12-31' },
    ],
    aiInsight: "Citadel's Q4 2025 shows an aggressive pivot into AI — opening a $456M NVDA position and a $372M PLTR position from scratch. Turnover is extremely high at 42.8%, typical of their multi-strategy quant approach. They're reducing broad market exposure (SPY -$1.08B) while increasing single-name tech bets. The 12.87% QoQ portfolio growth significantly outperformed the S&P 500, driven by META (+22%) and new AI positions.",
  },
};

// Fallback for any institution not in mock data
const generateDefaultInstitution = (name) => ({
  name, cik: '0000000000', type: 'Investment Advisor',
  headquarters: 'New York, NY', founded: '2000', ceo: 'Unknown',
  aum: 500000000000, portfolioValue: 280000000000,
  totalHoldings: 2000, topHolding: 'AAPL', topWeight: 5.0,
  qoqChange: 2.5, yoyChange: 12.0, turnoverRate: 8.5,
  newPositions: 80, closedPositions: 60, increasedPositions: 800, decreasedPositions: 700,
  bio: `${name} is an institutional investor filing 13F reports with the SEC.`,
  filingHistory: [
    { quarter: 'Q4 2025', value: 280000000000, change: 2.5, holdings: 2000, filed: '2026-02-05' },
    { quarter: 'Q3 2025', value: 273000000000, change: 1.8, holdings: 1950, filed: '2025-11-14' },
    { quarter: 'Q2 2025', value: 268000000000, change: -0.5, holdings: 1980, filed: '2025-08-14' },
  ],
  sectorAllocation: [
    { sector: 'Technology', weight: 28.0, change: 1.0 },
    { sector: 'Healthcare', weight: 15.0, change: -0.5 },
    { sector: 'Financials', weight: 14.0, change: 0.3 },
    { sector: 'Consumer Disc.', weight: 11.0, change: 0.8 },
    { sector: 'Communication', weight: 8.0, change: 0.4 },
    { sector: 'Other', weight: 24.0, change: -2.0 },
  ],
  topHoldings: [
    { ticker: 'AAPL', company: 'Apple Inc.', shares: 5000000, value: 1485000000, weight: 5.0, change: 2.0, sharesChange: 100000 },
    { ticker: 'MSFT', company: 'Microsoft Corp.', shares: 3200000, value: 1310000000, weight: 4.7, change: 1.5, sharesChange: 50000 },
    { ticker: 'NVDA', company: 'NVIDIA Corp.', shares: 2800000, value: 399000000, weight: 3.8, change: 5.2, sharesChange: 200000 },
    { ticker: 'AMZN', company: 'Amazon.com', shares: 1800000, value: 411000000, weight: 3.2, change: 1.2, sharesChange: 30000 },
    { ticker: 'GOOGL', company: 'Alphabet Inc.', shares: 2100000, value: 410000000, weight: 2.8, change: -0.5, sharesChange: -20000 },
  ],
  recentMoves: [
    { ticker: 'NVDA', action: 'Added', shares: 200000, value: 28500000, date: '2025-12-31' },
    { ticker: 'GOOGL', action: 'Reduced', shares: -20000, value: -3900000, date: '2025-12-31' },
  ],
  aiInsight: `${name}'s latest 13F filing shows a typical index-aligned portfolio with slight overweight in technology. Portfolio changes are modest, suggesting a passive or semi-passive investment approach.`,
});

// =============================================================================
// HELPERS
// =============================================================================
const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatShares = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (Math.abs(num) >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `${(num / 1e3).toFixed(0)}K`;
  return num.toLocaleString();
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function InstitutionProfilePage() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('weight');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const holdingsPerPage = 10;

  const decodedName = decodeURIComponent(institutionId || '');
  const inst = institutionData[decodedName] || generateDefaultInstitution(decodedName);

  const sortedHoldings = useMemo(() => {
    return [...inst.topHoldings].sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [inst.topHoldings, sortBy, sortOrder]);

  const totalPages = Math.ceil(sortedHoldings.length / holdingsPerPage);
  const pagedHoldings = sortedHoldings.slice((currentPage - 1) * holdingsPerPage, currentPage * holdingsPerPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const maxFilingValue = Math.max(...inst.filingHistory.map(f => f.value), 1);

  return (
    <div className="institution-profile-page">
      {/* Back Header */}
      <div className="inp-header">
        <button className="inp-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
      </div>

      {/* Main Layout */}
      <div className="inp-layout">
        {/* Left Sidebar - Profile */}
        <div className="inp-sidebar">
          {/* Profile Card */}
          <div className="inp-profile-card">
            <div className="inp-avatar">
              <div className={`inp-avatar-circle ${inst.type.includes('Hedge') ? 'hedge' : inst.type.includes('Bank') ? 'bank' : 'advisor'}`}>
                <Building2 size={28} />
              </div>
            </div>
            <h1 className="inp-name">{inst.name}</h1>
            <span className={`inp-type-badge ${inst.type.includes('Hedge') ? 'hedge' : inst.type.includes('Bank') ? 'bank' : 'advisor'}`}>{inst.type}</span>

            <div className="inp-meta-grid">
              <div className="inp-meta-item">
                <span className="inp-meta-label">Headquarters</span>
                <span className="inp-meta-value">{inst.headquarters}</span>
              </div>
              <div className="inp-meta-item">
                <span className="inp-meta-label">Founded</span>
                <span className="inp-meta-value">{inst.founded}</span>
              </div>
              <div className="inp-meta-item">
                <span className="inp-meta-label">CEO</span>
                <span className="inp-meta-value">{inst.ceo}</span>
              </div>
              <div className="inp-meta-item">
                <span className="inp-meta-label">CIK</span>
                <span className="inp-meta-value">{inst.cik}</span>
              </div>
            </div>

            {inst.bio && <p className="inp-bio">{inst.bio}</p>}
          </div>

          {/* Portfolio Stats */}
          <div className="inp-stats-card">
            <h3 className="inp-card-title"><BarChart3 size={16} /> Portfolio Overview</h3>
            <div className="inp-stat-rows">
              <div className="inp-stat-row"><span>AUM</span><span className="inp-stat-val">{formatCurrency(inst.aum)}</span></div>
              <div className="inp-stat-row"><span>13F Portfolio Value</span><span className="inp-stat-val">{formatCurrency(inst.portfolioValue)}</span></div>
              <div className="inp-stat-row"><span>Total Holdings</span><span className="inp-stat-val">{inst.totalHoldings.toLocaleString()}</span></div>
              <div className="inp-stat-row"><span>QoQ Change</span><span className={`inp-stat-val ${inst.qoqChange >= 0 ? 'positive' : 'negative'}`}>{inst.qoqChange >= 0 ? '+' : ''}{inst.qoqChange}%</span></div>
              <div className="inp-stat-row"><span>YoY Change</span><span className={`inp-stat-val ${inst.yoyChange >= 0 ? 'positive' : 'negative'}`}>{inst.yoyChange >= 0 ? '+' : ''}{inst.yoyChange}%</span></div>
              <div className="inp-stat-row"><span>Turnover Rate</span><span className="inp-stat-val">{inst.turnoverRate}%</span></div>
            </div>
          </div>

          {/* Position Changes */}
          <div className="inp-stats-card">
            <h3 className="inp-card-title"><Activity size={16} /> Position Changes (QoQ)</h3>
            <div className="inp-change-grid">
              <div className="inp-change-item new"><span className="inp-change-num">{inst.newPositions}</span><span>New</span></div>
              <div className="inp-change-item closed"><span className="inp-change-num">{inst.closedPositions}</span><span>Closed</span></div>
              <div className="inp-change-item increased"><span className="inp-change-num">{inst.increasedPositions.toLocaleString()}</span><span>Increased</span></div>
              <div className="inp-change-item decreased"><span className="inp-change-num">{inst.decreasedPositions.toLocaleString()}</span><span>Decreased</span></div>
            </div>
          </div>

          {/* Sector Allocation */}
          <div className="inp-stats-card">
            <h3 className="inp-card-title"><PieChart size={16} /> Sector Allocation</h3>
            <div className="inp-sector-list">
              {inst.sectorAllocation.map((s, idx) => (
                <div key={idx} className="inp-sector-row">
                  <div className="inp-sector-info">
                    <span className="inp-sector-name">{s.sector}</span>
                    <span className={`inp-sector-change ${s.change >= 0 ? 'positive' : 'negative'}`}>{s.change >= 0 ? '+' : ''}{s.change}%</span>
                  </div>
                  <div className="inp-sector-bar-bg"><div className="inp-sector-bar" style={{ width: `${(s.weight / 35) * 100}%` }}></div></div>
                  <span className="inp-sector-weight">{s.weight}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="inp-content">
          {/* AI Insight */}
          <div className="inp-ai-card">
            <div className="inp-ai-header">
              <div className="inp-ai-badge"><Brain size={14} /> AI Insight</div>
            </div>
            <p className="inp-ai-text">{inst.aiInsight}</p>
          </div>

          {/* Filing History Timeline */}
          <div className="inp-panel">
            <h3 className="inp-panel-title"><Calendar size={16} /> 13F Filing History</h3>
            <div className="inp-filing-timeline">
              {inst.filingHistory.map((f, idx) => (
                <div key={idx} className="inp-filing-row">
                  <div className="inp-filing-quarter">{f.quarter}</div>
                  <div className="inp-filing-bar-wrap">
                    <div className="inp-filing-bar" style={{ width: `${(f.value / maxFilingValue) * 100}%` }}></div>
                  </div>
                  <div className="inp-filing-value">{formatCurrency(f.value)}</div>
                  <div className={`inp-filing-change ${f.change >= 0 ? 'positive' : 'negative'}`}>{f.change >= 0 ? '+' : ''}{f.change}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Moves */}
          <div className="inp-panel">
            <h3 className="inp-panel-title"><Briefcase size={16} /> Recent Moves</h3>
            <div className="inp-moves-grid">
              {inst.recentMoves.map((move, idx) => (
                <div key={idx} className="inp-move-card" onClick={() => navigate(`/stock/${move.ticker}?tab=institutions`)}>
                  <div className="inp-move-top">
                    <span className="inp-move-ticker">{move.ticker}</span>
                    <span className={`inp-move-action ${move.action === 'Reduced' || move.action === 'Closed' ? 'negative' : 'positive'}`}>{move.action}</span>
                  </div>
                  <div className="inp-move-stats">
                    <span>{formatShares(Math.abs(move.shares))} shares</span>
                    <span>{formatCurrency(Math.abs(move.value))}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Holdings Table */}
          <div className="inp-panel">
            <h3 className="inp-panel-title"><Layers size={16} /> Top Holdings <span className="inp-count">{inst.topHoldings.length}</span></h3>
            <div className="inp-table-wrap">
              <table className="inp-table">
                <thead>
                  <tr>
                    <th>Stock</th>
                    <th onClick={() => handleSort('shares')}>Shares <SortIcon field="shares" /></th>
                    <th onClick={() => handleSort('value')}>Value <SortIcon field="value" /></th>
                    <th onClick={() => handleSort('weight')}>Weight <SortIcon field="weight" /></th>
                    <th onClick={() => handleSort('change')}>QoQ Change <SortIcon field="change" /></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedHoldings.map((h, idx) => (
                    <tr key={idx} onClick={() => navigate(`/stock/${h.ticker}?tab=institutions`)}>
                      <td>
                        <div className="inp-holding-name">{h.ticker}</div>
                        <div className="inp-holding-company">{h.company}</div>
                      </td>
                      <td>
                        <div>{formatShares(h.shares)}</div>
                        <div className={`inp-shares-change ${h.sharesChange >= 0 ? 'positive' : 'negative'}`}>
                          {h.sharesChange >= 0 ? '+' : ''}{formatShares(h.sharesChange)}
                        </div>
                      </td>
                      <td>{formatCurrency(h.value)}</td>
                      <td>{h.weight}%</td>
                      <td>
                        <span className={`inp-change ${h.change === 999 ? 'new-pos' : h.change >= 0 ? 'positive' : 'negative'}`}>
                          {h.change === 999 ? 'NEW' : `${h.change >= 0 ? '+' : ''}${h.change}%`}
                        </span>
                      </td>
                      <td>
                        <button className="inp-view-btn" onClick={(e) => { e.stopPropagation(); navigate(`/stock/${h.ticker}?tab=institutions`); }}>
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="inp-pagination">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
                <span>Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}